/* =============================================================================
   Three Cups custom components — light JS

   Loaded once from layout/theme.liquid (defer). Currently:
     - Lightweight analytics: events from data-track attributes + Shopify cart
       events, pushed to window.dataLayer (GA4-ready) and window.tcEvents.
   ============================================================================= */

(function () {
  'use strict';

  // ─── Lightweight analytics ─────────────────────────────────────────────
  //
  // Brief §10. Events fire to:
  //   1. window.dataLayer (GA4 / GTM, if loaded)
  //   2. window.tcEvents (in-memory log for debugging)
  //   3. console.debug (in development — guarded)
  //
  // To track a click on any element:
  //   <a data-track="homepage_cta_clicked"
  //      data-track-cta_name="find_your_tea"
  //      data-track-section="hero">…</a>

  window.tcEvents = window.tcEvents || [];
  window.dataLayer = window.dataLayer || [];

  function tcTrack(name, props) {
    const event = Object.assign({ event: name, ts: Date.now() }, props || {});
    window.tcEvents.push(event);
    window.dataLayer.push(event);
    if (window.tcDebug) console.debug('[tc]', event);
  }
  window.tcTrack = tcTrack;

  // Click delegation for data-track elements
  function readTrackProps(el) {
    const props = {};
    for (const attr of el.attributes) {
      if (attr.name.startsWith('data-track-')) {
        props[attr.name.slice('data-track-'.length).replaceAll('-', '_')] = attr.value;
      }
    }
    return props;
  }

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target.closest('[data-track]');
      if (!target) return;
      const name = target.getAttribute('data-track');
      if (!name) return;
      tcTrack(name, readTrackProps(target));
    },
    { capture: true },
  );

  // ─── Listen to Maestrooo's cart + variant events ─────────────────────
  // (See CLAUDE.md — `variant:add`, `cart:change`, `line-item:change`.)

  document.addEventListener('variant:add', (e) => {
    const items = (e.detail && e.detail.items) || [];
    for (const item of items) {
      tcTrack('product_added_to_cart', {
        product_id: item.product_id,
        variant_id: item.variant_id,
        product_title: item.product_title,
        price: item.final_line_price || item.price,
        quantity: item.quantity,
        source: 'pdp',
      });
    }
  });

  document.addEventListener('variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    tcTrack('variant_changed', {
      variant_id: v.id,
      product_id: v.product_id,
      price: v.price,
    });
  });

  document.addEventListener('cart:change', (e) => {
    const cart = e.detail && e.detail.cart;
    if (!cart) return;
    tcTrack('cart_changed', {
      base_event: e.detail.baseEvent,
      item_count: cart.item_count,
      total_price: cart.total_price,
    });
  });

  // Subscription selection on PDP — fired when subscription radio toggles.
  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!t.matches('input[type="radio"][name*="selling_plan"]')) return;
      const planLabel = (t.closest('label') || t.parentElement).innerText.trim();
      tcTrack('subscription_selected', {
        plan: planLabel.slice(0, 80),
        product_id: document.querySelector('[name="product-id"]')?.value,
      });
    },
    { capture: true },
  );

  // Brew guide views — any link to /brew/* fires a track on click.
  document.addEventListener(
    'click',
    (e) => {
      const a = e.target.closest('a[href*="/brew/"]');
      if (!a) return;
      tcTrack('brew_guide_viewed', { tea: a.href.split('/brew/').pop() });
    },
    { capture: true },
  );

  // ─── Quiz result card → PNG renderer ────────────────────────────────
  // Draws a 1080×1350 (4:5) shareable card directly onto canvas using the
  // same composition as the on-page card. No external dependencies.
  function renderCardImage(profile, key, teaTitles) {
    return loadTcLogo().then((logoImg) => new Promise((resolve) => {
      const W = 1080;
      const H = 1350;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      const PAPER = '#ffffff';
      const INK = '#161616';
      const MUTED = 'rgba(22,22,22,0.55)';
      const RULE = 'rgba(22,22,22,0.16)';
      const FAINT = 'rgba(22,22,22,0.08)';

      // White card background
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, W, H);

      // Top accent band (profile colour)
      ctx.fillStyle = profile.colour;
      ctx.fillRect(0, 0, W, 28);

      // Outer 1px border (matches on-page .tc-card border)
      ctx.strokeStyle = RULE;
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

      // Faint inset hairlines top + bottom (mirrors on-page ::before/::after)
      ctx.strokeStyle = FAINT;
      ctx.beginPath();
      ctx.moveTo(40, 64);
      ctx.lineTo(W - 40, 64);
      ctx.moveTo(40, H - 40);
      ctx.lineTo(W - 40, H - 40);
      ctx.stroke();

      const padX = 100;
      let y = 152;

      // Header: brand logo PNG (uploaded brand asset) + edition number
      ctx.textBaseline = 'top';
      const logoW = 200;
      let logoH = logoW * 0.5;
      if (logoImg) {
        const ratio = logoImg.height / logoImg.width;
        logoH = logoW * ratio;
        ctx.drawImage(logoImg, padX, y, logoW, logoH);
      }
      ctx.font = '500 20px Inter, system-ui, sans-serif';
      ctx.fillStyle = MUTED;
      const edition = ('Edition ' + (profile.edition || '—')).toUpperCase();
      const editionW = ctx.measureText(edition).width;
      // Vertically centre the edition text against the logo height
      ctx.fillText(edition, W - padX - editionW, y + (logoH - 22) / 2);
      y += Math.max(130, logoH + 30);

      // Eyebrow
      ctx.font = '500 20px Inter, system-ui, sans-serif';
      ctx.fillStyle = MUTED;
      ctx.fillText('YOUR TEA PROFILE', padX, y);
      y += 64;

      // Big profile name (wrap if needed)
      ctx.fillStyle = INK;
      const nameSize = profile.name.length > 18 ? 102 : 130;
      ctx.font = `500 ${nameSize}px "Times New Roman", Times, serif`;
      const lines = wrapLine(ctx, profile.name + '.', W - padX * 2);
      lines.forEach((line, i) => {
        ctx.fillText(line, padX, y + i * (nameSize * 1.0));
      });
      y += lines.length * nameSize + 28;

      // Tagline
      ctx.fillStyle = INK;
      ctx.font = '400 30px Inter, system-ui, sans-serif';
      const taglineLines = wrapLine(ctx, profile.tagline, W - padX * 2);
      taglineLines.forEach((line, i) => {
        ctx.globalAlpha = 0.78;
        ctx.fillText(line, padX, y + i * 42);
      });
      ctx.globalAlpha = 1;
      y += taglineLines.length * 42 + 56;

      // Rule
      ctx.strokeStyle = RULE;
      ctx.beginPath();
      ctx.moveTo(padX, y);
      ctx.lineTo(W - padX, y);
      ctx.stroke();
      y += 48;

      // Eyebrow: three teas
      ctx.font = '500 20px Inter, system-ui, sans-serif';
      ctx.fillStyle = MUTED;
      ctx.fillText('THREE TEAS TO START', padX, y);
      y += 56;

      // Tea list
      teaTitles.forEach((title, i) => {
        const num = String(i + 1).padStart(2, '0');
        ctx.font = '500 18px Inter, system-ui, sans-serif';
        ctx.fillStyle = MUTED;
        ctx.fillText(num, padX, y + 12);
        ctx.font = '500 42px "Times New Roman", Times, serif';
        ctx.fillStyle = INK;
        ctx.fillText(title, padX + 70, y);
        if (i < teaTitles.length - 1) {
          ctx.strokeStyle = 'rgba(22,22,22,0.08)';
          ctx.beginPath();
          ctx.moveTo(padX, y + 64);
          ctx.lineTo(W - padX, y + 64);
          ctx.stroke();
        }
        y += 64;
      });

      // Footer position: pin the brew tip just above it.
      const footY = H - 112;
      const tipY = footY - 64;
      ctx.font = 'italic 400 26px "Times New Roman", Times, serif';
      ctx.fillStyle = INK;
      ctx.globalAlpha = 0.78;
      const tipLines = wrapLine(ctx, '"' + profile.brew_tip + '"', W - padX * 2);
      // Render tip lines upward from tipY
      const tipBlockHeight = tipLines.length * 34;
      const tipStartY = tipY - tipBlockHeight + 34;
      tipLines.forEach((line, i) => {
        ctx.fillText(line, padX, tipStartY + i * 34);
      });
      ctx.globalAlpha = 1;

      // Dashed rule above tip
      ctx.strokeStyle = RULE;
      ctx.setLineDash([4, 8]);
      ctx.beginPath();
      ctx.moveTo(padX, tipStartY - 32);
      ctx.lineTo(W - padX, tipStartY - 32);
      ctx.stroke();
      ctx.setLineDash([]);

      // Footer
      ctx.font = '500 18px Inter, system-ui, sans-serif';
      ctx.fillStyle = MUTED;
      ctx.fillText('THREECUPSTEA.COM', padX, footY);
      const stamp = 'MMXXVI';
      const stampW = ctx.measureText(stamp).width;
      ctx.fillText(stamp, W - padX - stampW, footY);

      canvas.toBlob((b) => resolve(b), 'image/png');
    }));
  }

  // Cached promise so we only fetch the logo PNG once.
  const TC_LOGO_URL = '/cdn/shop/files/three_cups_logo_black.png?v=1731186630&width=600';
  let tcLogoPromise = null;
  function loadTcLogo() {
    if (tcLogoPromise) return tcLogoPromise;
    tcLogoPromise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = TC_LOGO_URL;
    });
    return tcLogoPromise;
  }

  function wrapLine(ctx, text, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let cur = '';
    for (const w of words) {
      const test = cur ? cur + ' ' + w : w;
      if (ctx.measureText(test).width > maxWidth && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  }

  // ─── Tea quiz controller ────────────────────────────────────────────
  function initQuiz() {
    const form = document.querySelector('[data-tc-quiz]');
    if (!form) return;

    const cfgEl = document.querySelector('[data-tc-quiz-config]');
    const cfg = cfgEl ? JSON.parse(cfgEl.textContent) : { profiles: {} };

    const section = document.querySelector('[data-tc-quiz-section]') || form.closest('.tc-quiz');
    const steps = Array.from(form.querySelectorAll('.tc-quiz__step, .tc-quiz__result'));
    const nextBtn = form.querySelector('[data-tc-quiz-next]');
    const prevBtn = form.querySelector('[data-tc-quiz-prev]');
    const navWrap = form.querySelector('[data-tc-quiz-nav]');
    const barFill = form.querySelector('[data-tc-quiz-bar-fill]');
    const startBtn = form.querySelector('[data-tc-quiz-start]');
    // Question steps are the ones with a numeric data-step (1..6).
    const questionSteps = steps.filter((s) => /^\d+$/.test(s.dataset.step));
    const questionCount = questionSteps.length;
    let pendingAdvance = null;

    let current = 0;

    function stepKind(step) {
      const k = step.dataset.step;
      if (k === 'intro') return 'intro';
      if (k === 'email') return 'email';
      if (k === 'result') return 'result';
      return 'question';
    }

    function show(i) {
      // Restart entrance animation on every change.
      steps.forEach((s, idx) => {
        const isActive = idx === i;
        s.hidden = !isActive;
        if (isActive) {
          s.classList.remove('is-active');
          void s.offsetWidth;
          s.classList.add('is-active');
        } else {
          s.classList.remove('is-active');
        }
      });
      const kind = stepKind(steps[i]);
      const isResult = kind === 'result';
      const isIntro = kind === 'intro';
      const isEmail = kind === 'email';
      const isQuestion = kind === 'question';
      section?.classList.toggle('is-result', isResult);
      section?.classList.toggle('is-intro', isIntro);

      // The intro step has its own inline Start button — hide nav there.
      navWrap.hidden = isResult || isIntro;
      prevBtn.hidden = isIntro || isResult || i === 0;
      const hintEl = form.querySelector('.tc-quiz__hint');
      if (hintEl) hintEl.hidden = !isQuestion;

      if (isQuestion) {
        const qNum = parseInt(steps[i].dataset.step, 10);
        const isLastQuestion = qNum === questionCount;
        nextBtn.textContent = isLastQuestion ? 'Continue →' : 'Next';
      } else if (isEmail) {
        nextBtn.textContent = 'See my profile →';
      } else {
        nextBtn.textContent = 'Next';
      }
      nextBtn.disabled = !stepHasAnswer(steps[i]);

      if (barFill) {
        let pct = 0;
        if (isQuestion) {
          const qNum = parseInt(steps[i].dataset.step, 10);
          pct = Math.round(((qNum - 1) / questionCount) * 100);
        } else if (isEmail) {
          pct = 100;
        }
        barFill.style.width = pct + '%';
      }

      // Focus the right element when entering a step.
      requestAnimationFrame(() => {
        if (isQuestion) {
          const checked = steps[i].querySelector('input[type="radio"]:checked');
          const firstRadio = steps[i].querySelector('input[type="radio"]');
          (checked || firstRadio)?.focus({ preventScroll: true });
        } else if (isEmail) {
          steps[i].querySelector('[data-tc-quiz-email]')?.focus({ preventScroll: true });
        } else if (isIntro) {
          startBtn?.focus({ preventScroll: true });
        }
      });
    }

    function stepHasAnswer(step) {
      const kind = stepKind(step);
      if (kind === 'intro' || kind === 'result') return true;
      if (kind === 'email') {
        const input = step.querySelector('[data-tc-quiz-email]');
        return !!(input && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value.trim()));
      }
      const radios = step.querySelectorAll('input[type="radio"]');
      if (!radios.length) return true;
      return Array.from(radios).some((r) => r.checked);
    }

    function tally() {
      const counts = {};
      form.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
        const w = r.dataset.w || 'balanced';
        counts[w] = (counts[w] || 0) + 1;
      });
      // Pick the highest-count profile.
      let best = 'balanced';
      let bestN = -1;
      for (const k in counts) {
        if (counts[k] > bestN) { best = k; bestN = counts[k]; }
      }
      return best;
    }

    function renderResult(forcedKey) {
      const key = forcedKey && cfg.profiles[forcedKey] ? forcedKey : tally();
      const profile = cfg.profiles[key] || cfg.profiles.balanced;
      const result = form.querySelector('.tc-quiz__result');
      const card = result.querySelector('[data-tc-quiz-card]');

      result.querySelector('[data-tc-quiz-name]').textContent = profile.name + '.';
      result.querySelector('[data-tc-quiz-tagline]').textContent = profile.tagline;
      result.querySelector('[data-tc-quiz-bar]').style.background = profile.colour;
      result.querySelector('[data-tc-quiz-brewtip]').textContent = '"' + profile.brew_tip + '"';
      const editionEl = result.querySelector('[data-tc-quiz-edition]');
      if (editionEl) editionEl.textContent = 'Edition ' + (profile.edition || '—');
      if (card) card.style.setProperty('--tc-card-accent', profile.colour);

      const cards = result.querySelector('[data-tc-quiz-cards]');
      cards.innerHTML = '';
      const teaTitles = profile.teas.map((handle) => (cfg.titles && cfg.titles[handle]) || handle.replace(/-/g, ' '));
      profile.teas.forEach((handle, i) => {
        const li = document.createElement('li');
        li.className = 'tc-card__tea';
        const a = document.createElement('a');
        a.href = `/products/${handle}`;
        a.innerHTML = `<span class="tc-card__tea-name">${teaTitles[i]}</span><span class="tc-card__tea-arrow" aria-hidden="true">→</span>`;
        li.appendChild(a);
        cards.appendChild(li);
      });

      // Wire up download / share-image buttons.
      const downloadBtn = result.querySelector('[data-tc-quiz-download]');
      if (downloadBtn && !downloadBtn.dataset.tcBound) {
        downloadBtn.dataset.tcBound = '1';
        downloadBtn.addEventListener('click', () => {
          renderCardImage(profile, key, teaTitles).then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `three-cups-${key}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            tcTrack('referral_shared', { source: 'quiz_result', method: 'download_card' });
          });
        });
      }

      const shareImgBtn = result.querySelector('[data-tc-quiz-share-img]');
      if (shareImgBtn && !shareImgBtn.dataset.tcBound) {
        shareImgBtn.dataset.tcBound = '1';
        shareImgBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          const blob = await renderCardImage(profile, key, teaTitles);
          const file = new File([blob], `three-cups-${key}.png`, { type: 'image/png' });
          const text = `My Three Cups tea profile: ${profile.name}.`;
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              await navigator.share({ files: [file], title: 'My Three Cups tea profile', text });
              tcTrack('referral_shared', { source: 'quiz_result', method: 'share_image' });
            } catch (_) { /* user cancelled */ }
          } else {
            // Fallback: download the image
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `three-cups-${key}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }
        });
      }

      // Wire the "Shop these teas" CTA to a search filtered to the 3 picks.
      const ctaBtn = result.querySelector('[data-tc-quiz-cta]');
      if (ctaBtn) {
        // Storefront search treats space-separated terms as AND; "OR" is a keyword.
        // Use the product titles (with quotes) so we hit those teas precisely.
        const q = teaTitles.map((t) => `"${t}"`).join(' OR ');
        ctaBtn.href = `/search?type=product&q=${encodeURIComponent(q)}`;
      }

      tcTrack('quiz_completed', { profile: profile.name, recommended: profile.teas });

      // ─── Share URL ──────────────────────────────────────────────
      const params = new URLSearchParams({ profile: key });
      // Persist answers so the share link reproduces the same result.
      form.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
        params.set(r.name, r.value);
      });
      const baseUrl = location.origin + location.pathname;
      const shareUrl = `${baseUrl}?${params.toString()}`;
      const shareInput = result.querySelector('[data-tc-quiz-share-url]');
      if (shareInput) shareInput.value = shareUrl;

      const text = encodeURIComponent(`My Three Cups tea profile: ${profile.name}. ${shareUrl}`);
      const wa = result.querySelector('[data-tc-quiz-share-wa]');
      if (wa) wa.href = `https://wa.me/?text=${text}`;
      const mail = result.querySelector('[data-tc-quiz-share-mail]');
      if (mail) mail.href = `mailto:?subject=My%20tea%20profile&body=${text}`;
      const sms = result.querySelector('[data-tc-quiz-share-sms]');
      if (sms) sms.href = `sms:?body=${text}`;

      const copyBtn = result.querySelector('[data-tc-quiz-share-copy]');
      if (copyBtn && !copyBtn.dataset.tcBound) {
        copyBtn.dataset.tcBound = '1';
        copyBtn.addEventListener('click', () => {
          navigator.clipboard?.writeText(shareInput.value).catch(() => {});
          shareInput.select();
          copyBtn.textContent = 'Copied';
          setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 1500);
          tcTrack('referral_shared', { source: 'quiz_result', method: 'copy_link' });
        });
      }
    }

    function submitEmail(email) {
      // Tag the customer with their tea profile so we can segment in admin.
      const profileKey = tally();
      const fd = new FormData();
      fd.append('form_type', 'customer');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[tags]', 'tea-quiz, tea-quiz-' + profileKey);
      // Best-effort POST — don't block UX on response.
      try {
        fetch('/contact', {
          method: 'POST',
          body: fd,
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' },
        }).catch(() => {});
      } catch (_) {}
      tcTrack('quiz_email_submitted', { profile: profileKey });
    }

    function advance() {
      if (pendingAdvance) { clearTimeout(pendingAdvance); pendingAdvance = null; }
      const kind = stepKind(steps[current]);
      if (kind === 'result') return;
      if (!stepHasAnswer(steps[current])) {
        // Email step shows inline error; questions never reach here disabled.
        if (kind === 'email') {
          const errEl = steps[current].querySelector('[data-tc-quiz-email-error]');
          if (errEl) errEl.hidden = false;
        }
        return;
      }
      if (kind === 'email') {
        const input = steps[current].querySelector('[data-tc-quiz-email]');
        submitEmail(input.value.trim());
      }
      // Find next non-result step or render result.
      const nextIdx = current + 1;
      if (nextIdx >= steps.length) return;
      const nextStep = steps[nextIdx];
      if (stepKind(nextStep) === 'result') {
        renderResult();
      }
      current = nextIdx;
      show(current);
    }

    function goBack() {
      if (pendingAdvance) { clearTimeout(pendingAdvance); pendingAdvance = null; }
      const kind = stepKind(steps[current]);
      if (kind === 'result' || kind === 'intro' || current === 0) return;
      current--;
      show(current);
    }

    nextBtn.addEventListener('click', advance);
    prevBtn.addEventListener('click', goBack);
    startBtn?.addEventListener('click', () => {
      tcTrack('quiz_started', {});
      advance();
    });

    // "Skip" on the email step jumps to the result without submitting an email.
    form.querySelector('[data-tc-quiz-email-skip]')?.addEventListener('click', () => {
      if (pendingAdvance) { clearTimeout(pendingAdvance); pendingAdvance = null; }
      tcTrack('quiz_email_skipped', {});
      const nextIdx = current + 1;
      const nextStep = steps[nextIdx];
      if (nextStep && stepKind(nextStep) === 'result') {
        renderResult();
        current = nextIdx;
        show(current);
      }
    });

    form.addEventListener('change', (e) => {
      if (!e.target.matches('input[type="radio"]')) return;
      nextBtn.disabled = !stepHasAnswer(steps[current]);
      // Auto-advance after picking a radio, with a small confirmation beat for
      // the last question.
      const isLastQuestion = stepKind(steps[current]) === 'question'
        && parseInt(steps[current].dataset.step, 10) === questionCount;
      if (pendingAdvance) clearTimeout(pendingAdvance);
      pendingAdvance = setTimeout(advance, isLastQuestion ? 500 : 320);
    });

    // Live email validation enables the Next button and clears errors.
    form.addEventListener('input', (e) => {
      if (e.target.matches('[data-tc-quiz-email]')) {
        nextBtn.disabled = !stepHasAnswer(steps[current]);
        const errEl = steps[current].querySelector('[data-tc-quiz-email-error]');
        if (errEl) errEl.hidden = true;
      }
    });

    // Keyboard: A/B/C/D or 1-4 to pick, Enter to advance, Backspace to go back.
    form.addEventListener('keydown', (e) => {
      const kind = stepKind(steps[current]);
      if (kind === 'result') return;
      const typingInTextField = e.target.matches('input[type="email"], input[type="text"], textarea');
      const radios = Array.from(steps[current].querySelectorAll('input[type="radio"]'));
      const key = e.key.toLowerCase();
      const letterIdx = 'abcdefgh'.indexOf(key);
      const numIdx = /^[1-9]$/.test(e.key) ? parseInt(e.key, 10) - 1 : -1;
      const idx = letterIdx >= 0 ? letterIdx : numIdx;
      // Letter/number shortcuts only when there are radios AND we're not typing into a text field.
      if (idx >= 0 && idx < radios.length && !typingInTextField) {
        e.preventDefault();
        const r = radios[idx];
        r.checked = true;
        r.focus({ preventScroll: true });
        r.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      if (e.key === 'Enter') {
        if (e.target === nextBtn) return;
        if (stepHasAnswer(steps[current])) {
          e.preventDefault();
          advance();
        }
      } else if (e.key === 'Backspace' && !typingInTextField) {
        if (kind !== 'intro' && current > 0) { e.preventDefault(); goBack(); }
      }
    });

    const restart = form.querySelector('[data-tc-quiz-restart]');
    if (restart) {
      restart.addEventListener('click', () => {
        form.reset();
        current = 0;
        show(0);
      });
    }

    // If the URL carries quiz answers (from a shared result link), pre-fill
    // and jump straight to the result.
    const params = new URLSearchParams(location.search);
    const sharedProfile = params.get('profile');
    if (sharedProfile || params.get('q1')) {
      let any = false;
      ['q1', 'q2', 'q3', 'q4', 'q5', 'q6'].forEach((q) => {
        const v = params.get(q);
        if (!v) return;
        const radio = form.querySelector(`input[name="${q}"][value="${v}"]`);
        if (radio) {
          radio.checked = true;
          any = true;
        }
      });
      if (any || sharedProfile) {
        renderResult(sharedProfile);
        current = steps.length - 1;
        show(current);
        return;
      }
    }

    show(0);
  }

  // ─── Scroll reveal (audit I1) ───────────────────────────────────────
  // Opt-in: any element with .tc-reveal fades + rises in as it enters the
  // viewport. We add .tc-reveal-ready to <html> so the hidden initial state
  // (in CSS) only applies while JS is running — no-JS users always see content.
  // We bail entirely under reduced-motion or without IntersectionObserver,
  // leaving content visible.
  function initReveal() {
    const els = document.querySelectorAll('.tc-reveal');
    if (!els.length) return;
    const reduce = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;

    document.documentElement.classList.add('tc-reveal-ready');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
  }

  // ─── In-detail card deck (Apple-style slider, powered by Swiper) ─────
  // Swiper is self-hosted and loaded only on product pages (see theme.liquid),
  // so guard on the global. slidesPerView:'auto' reads each card's CSS width;
  // arrows + clickable dots live outside the .swiper element, so we pass real
  // element refs. No Swiper / no deck → no-op (the markup degrades to static
  // cards).
  function initDeck() {
    if (!window.Swiper) return;
    document.querySelectorAll('.tc-detail').forEach((section) => {
      const el = section.querySelector('[data-tc-deck-swiper]');
      if (!el || el.swiper) return; // el.swiper is set by Swiper once initialized
      if (el.querySelectorAll('.swiper-slide').length < 2) return;
      const reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // The viewport is content-width on every size, so Swiper natively aligns
      // the first card to the left content margin and bounds the last at the
      // right margin — no slidesOffset inset (that inset was what pushed the
      // first card off-screen on mobile). On desktop, CSS (overflow:visible on
      // the viewport) lets cards bleed to the screen edges; mobile stays a clean
      // content-width slider with Swiper's touch-safe default clipping.
      // eslint-disable-next-line no-new
      new window.Swiper(el, {
        slidesPerView: 'auto',
        spaceBetween: 24,
        grabCursor: true,
        threshold: 4,
        speed: reduce ? 0 : 460,
        a11y: { enabled: true },
        keyboard: { enabled: true, onlyInViewport: true },
        navigation: {
          prevEl: section.querySelector('[data-tc-deck-prev]'),
          nextEl: section.querySelector('[data-tc-deck-next]'),
          disabledClass: 'is-disabled',
        },
        pagination: {
          el: section.querySelector('[data-tc-deck-dots]'),
          clickable: true,
          bulletClass: 'tc-detail__dot',
          bulletActiveClass: 'is-active',
        },
      });
    });
  }

  // ─── Set "inside the box" per-item image carousels ───────────────────
  // Each component item's image is a small Swiper the customer slides through
  // (clickable square dots + swipe; NO autoplay — no ambient motion on the set
  // page, which keeps it calm and WCAG 2.2.2-friendly). Guard on Swiper; a
  // single-image item (<2 slides) stays a static image.
  function initSetCarousels() {
    if (!window.Swiper) return;
    document.querySelectorAll('[data-tc-carousel]').forEach((el) => {
      if (el.swiper) return;
      if (el.querySelectorAll('.swiper-slide').length < 2) return;
      const reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // eslint-disable-next-line no-new
      new window.Swiper(el, {
        slidesPerView: 1,
        loop: true,
        grabCursor: true,
        speed: reduce ? 0 : 500,
        a11y: { enabled: true },
        pagination: {
          el: el.querySelector('.tc-set-item__dots'),
          clickable: true,
          bulletClass: 'tc-set-item__dot',
          bulletActiveClass: 'is-active',
        },
      });
    });
  }

  // ─── Info tooltips (reusable; spec + brew explainers) ────────────────
  // Each tc-tip is a <button popovertarget> + a popover bubble. The native
  // popovertarget toggles the bubble on tap/click for EVERY pointer type — the
  // reliable mobile path — and the auto-popover gives Escape / outside-tap
  // dismissal for free. JS only (1) positions the bubble under its trigger and
  // (2) adds hover/focus preview on fine pointers. All listeners are delegated on
  // `document` (capture phase for the non-bubbling `toggle`) so tips injected when
  // the buy panel re-renders keep working without re-binding. No popover support →
  // the buttons are inert (graceful).
  function initTips() {
    if (!('showPopover' in HTMLElement.prototype)) return;

    const triggerFor = (pop) =>
      document.querySelector('[aria-describedby="' + pop.id + '"]');

    function place(trigger, pop) {
      const t = trigger.getBoundingClientRect();
      const p = pop.getBoundingClientRect();
      const gap = 8;
      let top = t.bottom + gap;
      if (top + p.height > window.innerHeight - 8 && t.top - gap - p.height > 8) {
        top = t.top - gap - p.height; // flip above when there's no room below
      }
      let left = Math.min(t.left, window.innerWidth - p.width - 8);
      left = Math.max(8, left);
      pop.style.top = Math.round(top) + 'px';
      pop.style.left = Math.round(left) + 'px';
    }

    // Position a bubble as it opens. Capture: `toggle` doesn't bubble, and
    // capture still reaches bubbles added after a panel re-render.
    document.addEventListener('toggle', (e) => {
      const pop = e.target;
      if (e.newState !== 'open') return;
      if (!pop.classList || !pop.classList.contains('tc-tip__bubble')) return;
      const trigger = triggerFor(pop);
      if (trigger) place(trigger, pop);
    }, true);

    const reposition = () => {
      document.querySelectorAll('.tc-tip__bubble:popover-open').forEach((pop) => {
        const trigger = triggerFor(pop);
        if (trigger) place(trigger, pop);
      });
    };
    window.addEventListener('scroll', reposition, { passive: true });
    window.addEventListener('resize', reposition);

    // Fine pointers (mouse): preview on hover + focus. Touch uses the native
    // popovertarget tap-toggle (no click handling here — which also avoids the
    // auto-popover light-dismiss vs. manual-toggle race that broke tap-to-close).
    const fine = window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    let closeT;
    const overTip = (el) =>
      el && el.closest && (el.closest('[data-tc-tip]') || el.closest('.tc-tip__bubble'));
    const popFromTrigger = (el) => {
      const trigger = el && el.closest && el.closest('[data-tc-tip]');
      return trigger ? document.getElementById(trigger.getAttribute('aria-describedby')) : null;
    };
    const safeShow = (pop) => { if (pop) { try { pop.showPopover(); } catch (e) { /* open */ } } };

    document.addEventListener('pointerover', (e) => {
      if (!overTip(e.target)) return;
      clearTimeout(closeT);
      safeShow(popFromTrigger(e.target)); // null when hovering the bubble → just keep open
    });
    document.addEventListener('pointerout', (e) => {
      if (!overTip(e.target)) return;
      closeT = setTimeout(() => {
        document.querySelectorAll('.tc-tip__bubble:popover-open')
          .forEach((pop) => { try { pop.hidePopover(); } catch (e) { /* closed */ } });
      }, 150);
    });
    document.addEventListener('focusin', (e) => { safeShow(popFromTrigger(e.target)); });
    document.addEventListener('focusout', (e) => {
      const pop = popFromTrigger(e.target);
      if (pop) { try { pop.hidePopover(); } catch (e) { /* closed */ } }
    });
  }

  // ─── Journal story slider: infinite loop + timed auto-advance ───────────
  // Apple home-gallery behaviour: a neighbour peeks on BOTH sides at every
  // position and the loop wraps in either direction. Implementation: the last
  // slide is cloned before the first and vice versa; whenever the scroller
  // settles on a clone we teleport (instant scroll) to its real counterpart —
  // the viewer never sees the jump. Native scroll keeps mobile swipe momentum.
  // The active dot's pill fill (CSS) mirrors INTERVAL; autoplay runs only
  // while on screen; pause/play is the WCAG 2.2.2 control;
  // prefers-reduced-motion starts paused.
  function initJournalCarousel() {
    const INTERVAL = 5000; // keep in sync with --tc-journal-interval (CSS)
    document.querySelectorAll('.tc-journal').forEach((section) => {
      const track = section.querySelector('[data-tc-journal-track]');
      if (!track) return;
      const real = Array.from(track.children);
      const n = real.length;
      const toggle = section.querySelector('.tc-journal__playpause');
      const dots = Array.from(section.querySelectorAll('.tc-journal__dots button'));
      if (n < 2) { if (toggle) toggle.hidden = true; return; }

      // Edge clones (decorative duplicates: hidden from AT, not tabbable).
      const cloneLast = real[n - 1].cloneNode(true);
      const cloneFirst = real[0].cloneNode(true);
      [cloneLast, cloneFirst].forEach((c) => {
        c.setAttribute('aria-hidden', 'true');
        c.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
      });
      track.prepend(cloneLast);
      track.append(cloneFirst);
      const cells = Array.from(track.children); // [cloneLast, ...real, cloneFirst]

      const reduce = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let playing = !reduce;
      let visible = false;
      let timer = null;
      let index = 0; // real index 0..n-1

      const targetLeft = (cell) =>
        cell.offsetLeft - (track.clientWidth - cell.offsetWidth) / 2;
      const paint = () => {
        dots.forEach((d, i) =>
          d.setAttribute('aria-current', i === index ? 'true' : 'false'));
      };
      const goTo = (realI, behavior) => {
        index = ((realI % n) + n) % n;
        paint();
        track.scrollTo({ left: targetLeft(cells[index + 1]), behavior });
      };
      const restart = () => {
        clearInterval(timer);
        timer = null;
        section.classList.toggle('tc-journal--paused', !playing);
        if (toggle) toggle.setAttribute('aria-label', playing ? 'Pause' : 'Play');
        if (playing && visible) {
          timer = setInterval(() => {
            if (index === n - 1) {
              // Wrap forward: glide onto the first-slide clone; the settle
              // handler below teleports to the real first slide.
              index = 0;
              paint();
              track.scrollTo({ left: targetLeft(cells[n + 1]), behavior: 'smooth' });
            } else {
              goTo(index + 1, 'smooth');
            }
          }, INTERVAL);
        }
      };

      // Settle detection: after scrolling stops, normalise clones → real
      // slides and sync the dots (covers swipe, trackpad and autoplay).
      let settleT;
      track.addEventListener('scroll', () => {
        clearTimeout(settleT);
        settleT = setTimeout(() => {
          const centre = track.scrollLeft + track.clientWidth / 2;
          let nearest = 0;
          let best = Infinity;
          cells.forEach((cell, i) => {
            const d = Math.abs(cell.offsetLeft + cell.offsetWidth / 2 - centre);
            if (d < best) { best = d; nearest = i; }
          });
          if (nearest === 0) {
            goTo(n - 1, 'instant'); // was on the last-slide clone
          } else if (nearest === n + 1) {
            goTo(0, 'instant'); // was on the first-slide clone
          } else if (nearest - 1 !== index) {
            index = nearest - 1;
            paint();
            restart(); // user navigated — restart the cadence from here
          }
        }, 90);
      }, { passive: true });

      dots.forEach((d, i) => d.addEventListener('click', () => {
        goTo(i, 'smooth');
        restart();
      }));
      if (toggle) {
        toggle.addEventListener('click', () => { playing = !playing; restart(); });
      }
      new IntersectionObserver((entries) => {
        visible = entries.some((e) => e.isIntersecting);
        restart();
      }, { threshold: 0.35 }).observe(section);

      let resizeT;
      window.addEventListener('resize', () => {
        clearTimeout(resizeT);
        resizeT = setTimeout(() => goTo(index, 'instant'), 120);
      });

      goTo(0, 'instant');
      restart();
    });
  }

  // ── Free-shipping bar money format ─────────────────────────────────────
  // Maestrooo's <free-shipping-bar> formats via Intl with Shopify.locale
  // ("en" → "€32.00"), while Liquid prices use the shop format ("€16,00").
  // Normalise the bar's amount to comma decimals so the drawer is consistent.
  function tcFixShippingBarFormat() {
    const fix = () => {
      document.querySelectorAll('free-shipping-bar').forEach((bar) => {
        // After replacement the pattern no longer matches, so the observer
        // callback settles instead of looping.
        if (/€\d+\.\d{2}/.test(bar.innerHTML)) {
          bar.innerHTML = bar.innerHTML.replace(/€(\d+)\.(\d{2})/g, '€$1,$2');
        }
      });
    };
    fix();
    // The cart drawer re-renders its contents wholesale on cart changes,
    // replacing the <free-shipping-bar> node — so observe the persistent
    // section wrappers, not the bar itself.
    document.querySelectorAll('free-shipping-bar').forEach((bar) => {
      const scope = bar.closest('.shopify-section') || bar.parentElement;
      new MutationObserver(fix).observe(scope, { childList: true, subtree: true, characterData: true });
    });
    document.addEventListener('cart:change', () => setTimeout(fix, 150));
  }

  function tcInit() {
    initQuiz();
    initReveal();
    initDeck();
    initSetCarousels();
    initJournalCarousel();
    initTips();
    tcFixShippingBarFormat();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tcInit);
  } else {
    tcInit();
  }
})();
